import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { CalendarDays, UtensilsCrossed, Info } from 'lucide-react';
import { supabase } from '../lib/supabase';

const daysOfWeek = [
  { id: 'mon', day: 'Понеделник' },
  { id: 'tue', day: 'Вторник' },
  { id: 'wed', day: 'Сряда' },
  { id: 'thu', day: 'Четвъртък' },
  { id: 'fri', day: 'Петък' }
];

const defaultLunchMenu: Record<string, Record<string, { title: string; price: number }[]>> = {
  'Понеделник': {
    'Супи': [{ title: 'Таратор / Пилешка супа', price: 4.50 }],
    'Готвено': [{ title: 'Свинско с ориз / Пиле с картофи', price: 8.50 }],
    'Десерти': [{ title: 'Крем карамел', price: 4.00 }]
  },
  'Вторник': {
    'Супи': [{ title: 'Боб чорба / Крем супа', price: 4.50 }],
    'Готвено': [{ title: 'Мусака / Пълнени чушки', price: 8.50 }],
    'Десерти': [{ title: 'Мляко с ориз', price: 4.00 }]
  },
  'Сряда': {
    'Супи': [{ title: 'Шкембе чорба / Супа топчета', price: 4.90 }],
    'Готвено': [{ title: 'Кюфтета по чирпански / Пиле фрикасе', price: 8.50 }],
    'Десерти': [{ title: 'Бисквитена торта', price: 4.50 }]
  },
  'Четвъртък': {
    'Супи': [{ title: 'Таратор / Гъбена супа', price: 4.50 }],
    'Готвено': [{ title: 'Ризото с пиле / Свинско със зеле', price: 8.50 }],
    'Десерти': [{ title: 'Плодова салата', price: 4.00 }]
  },
  'Петък': {
    'Супи': [{ title: 'Рибена чорба / Пилешка супа', price: 5.50 }],
    'Готвено': [{ title: 'Пържена риба с картофи / Боб с наденица', price: 9.50 }],
    'Десерти': [{ title: 'Палачинка с шоколад', price: 4.00 }]
  }
};

export function LunchMenuModule() {
  const [activeTab, setActiveTab] = useState(daysOfWeek[0].id);
  const [dbItems, setDbItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchLunchItems() {
      try {
        const { data, error } = await supabase
          .from('menu_items')
          .select('*')
          .eq('category', 'Обедно меню')
          .eq('is_active', true);
        
        if (error) throw error;
        if (data) {
          setDbItems(data);
        }
      } catch (err) {
        console.error('Error fetching lunch items:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchLunchItems();
  }, []);

  const activeDay = daysOfWeek.find((d) => d.id === activeTab)?.day || 'Понеделник';

  // Check if we have database lunch items for ANY day
  const hasDbLunchItems = dbItems.length > 0;

  // Render content based on availability
  const renderDayCategories = () => {
    if (hasDbLunchItems) {
      // Filter database items for the active day
      const dayItems = dbItems.filter(i => {
        const sub = i.sub_category || i.subCategory || '';
        return sub.startsWith(`${activeDay}:`);
      });

      if (dayItems.length === 0) {
        return (
          <div className="py-12 text-center text-gusto-dark/40 font-medium italic">
            Няма публикувано обедно меню за този ден.
          </div>
        );
      }

      return (
        <div className="space-y-6 md:space-y-8 flex-1">
          {["Супи", "Салати", "Готвено", "Десерти"].map((subcat) => {
            const subcatItems = dayItems.filter(i => {
              const sub = i.sub_category || i.subCategory || '';
              return sub === `${activeDay}:${subcat}`;
            });

            if (subcatItems.length === 0) return null;

            return (
              <div key={subcat} className="space-y-3">
                <h4 className="font-heading font-extrabold text-sm md:text-base text-gusto-gold uppercase tracking-wider border-b border-gusto-gold/20 pb-1.5 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-gusto-red rounded-full animate-pulse" />
                  {subcat}
                </h4>
                
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                  {subcatItems.map((item, index) => (
                    <li key={item.id || index} className="flex items-start justify-between gap-3 border-b border-gusto-gray/5 pb-3">
                      <div className="flex-1 min-w-0 pr-2">
                        <span className="font-semibold text-base text-gusto-dark block leading-snug">
                          {item.title}
                          {item.weight && (
                            <span className="text-xs text-gusto-dark/50 font-normal ml-2 font-mono">
                              ({item.weight})
                            </span>
                          )}
                        </span>
                        {item.description && (
                          <p className="text-xs md:text-sm text-gusto-dark/70 mt-0.5 leading-relaxed">
                            {item.description}
                          </p>
                        )}
                      </div>
                      <span className="font-heading font-bold whitespace-nowrap text-right shrink-0">
                        <span className="text-gusto-red text-base md:text-lg">{Number(item.price).toFixed(2)} €</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      );
    } else {
      // Fallback for default predefined items
      const dayDefaults = defaultLunchMenu[activeDay] || {};
      return (
        <div className="space-y-6 md:space-y-8 flex-1">
          {Object.entries(dayDefaults).map(([subcat, items]) => {
            if (items.length === 0) return null;

            return (
              <div key={subcat} className="space-y-3">
                <h4 className="font-heading font-extrabold text-sm md:text-base text-[#A67C43] uppercase tracking-wider border-b border-[#A67C43]/20 pb-1.5 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-gusto-red rounded-full" />
                  {subcat}
                </h4>
                
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                  {items.map((item, index) => (
                    <li key={index} className="flex items-start justify-between gap-3 border-b border-gusto-gray/5 pb-3">
                      <div className="flex-1 min-w-0 pr-2">
                        <span className="font-semibold text-base text-gusto-dark block leading-snug">
                          {item.title}
                        </span>
                      </div>
                      <span className="font-heading font-bold whitespace-nowrap text-right shrink-0">
                        <span className="text-gusto-red text-base md:text-lg">{item.price.toFixed(2)} €</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      );
    }
  };

  return (
    <div className="bg-white rounded-[24px] md:rounded-[32px] shadow-sm border border-gusto-gray/10 overflow-hidden w-full mx-auto flex flex-col md:flex-row mt-4">
      {/* Tabs */}
      <div className="grid grid-cols-5 md:flex md:flex-col bg-gusto-dark text-white p-2 md:p-6 gap-1 md:gap-4 md:w-72 shrink-0">
        {daysOfWeek.map((day) => (
          <button
            key={day.id}
            onClick={() => setActiveTab(day.id)}
            className={`px-1 py-3 md:px-5 md:py-4 rounded-xl md:rounded-2xl font-heading font-bold text-xs sm:text-sm md:text-base transition-all text-center md:text-left flex flex-col md:flex-row items-center justify-center md:justify-start gap-1 md:gap-3 ${
              activeTab === day.id 
                ? 'bg-gusto-red text-white shadow-lg shadow-gusto-red/20' 
                : 'text-white/60 hover:bg-white/10 hover:text-white'
            }`}
          >
            <span className={`hidden md:block w-2 h-2 rounded-full transition-colors ${activeTab === day.id ? 'bg-white' : 'bg-transparent'}`} />
            <span className="md:hidden">{day.day.substring(0, 3)}</span>
            <span className="hidden md:inline">{day.day}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="p-5 md:p-10 lg:p-12 flex-1 text-left bg-white relative min-h-[340px] md:min-h-[400px]">
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80">
            <div className="w-8 h-8 border-4 border-gusto-red border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              transition={{ duration: 0.2 }}
              className="h-full flex flex-col"
            >
              <h3 className="text-xl md:text-3xl font-heading font-bold text-gusto-dark border-b border-gusto-gray/10 pb-4 md:pb-5 mb-5 flex items-center justify-center md:justify-start gap-2 md:gap-3 text-center md:text-left">
                <UtensilsCrossed className="w-5 h-5 md:w-7 md:h-7 text-gusto-gold shrink-0 hidden md:block" />
                Меню за {activeDay.toLowerCase()}
              </h3>

              {renderDayCategories()}

              <div className="mt-8 md:mt-10 bg-gusto-dark/5 p-4 md:p-5 rounded-xl md:rounded-2xl flex items-start gap-3 md:gap-4 border border-gusto-dark/10">
                <Info className="w-5 h-5 md:w-6 md:h-6 text-gusto-red shrink-0 mt-0.5" />
                <p className="text-xs md:text-sm text-gusto-dark/70 font-medium leading-relaxed">
                  Примерно меню. Актуалното меню търпи промени според наличностите. За точна информация и поръчки: 0700 50 605.
                </p>
              </div>
            </motion.div>
          </AnimatePresence>
        )}
      </div>
    </div>
  );
}
